/**
 * The socket client.
 *
 * One class owns the connection, and everything the UI knows arrives through
 * the `onMessage` callback — components never touch the socket. Reconnection is
 * automatic with backoff, and every reconnect replays the same join sequence,
 * which works precisely because the server sends full snapshots rather than
 * diffs: a fresh connection and a resumed one are the same code path on both
 * ends.
 *
 * The socket opens before anyone has an account. A connection is anonymous
 * until it registers or logs in, and after that the session token is what
 * carries the identity — including across a reconnect, which is how a dropped
 * player finds their seat and their chips again.
 */

import { PROTOCOL_VERSION, type ClientMessage, type PlayerAction, type ServerMessage } from '@pf/protocol';

import { socketUrl } from './server.ts';

export type ConnectionStatus = 'connecting' | 'open' | 'closed' | 'unauthenticated';

export interface GameSocketOptions {
  readonly url: string;
  /** An existing session, if the browser has one. */
  readonly session?: string | undefined;
  readonly onMessage: (message: ServerMessage) => void;
  readonly onStatus: (status: ConnectionStatus) => void;
  /** A new session to remember, from a register or login. */
  readonly onSession: (token: string) => void;
  /** The stored session was refused; the UI must ask for credentials. */
  readonly onSessionRejected: () => void;
}

export class GameSocket {
  private socket: WebSocket | null = null;
  private closedByUser = false;
  private retryDelay = 500;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  /** Replayed after a reconnect, so the client lands back where it was. */
  private rejoin: ClientMessage | null = null;
  private session: string | undefined;
  private readonly options: GameSocketOptions;

  constructor(options: GameSocketOptions) {
    this.options = options;
    this.session = options.session;
    this.open();
  }

  private open(): void {
    this.options.onStatus('connecting');
    const socket = new WebSocket(this.options.url);
    this.socket = socket;

    socket.onopen = () => {
      this.retryDelay = 500;
      this.options.onStatus('open');
      // With a session we can say hello; without one the UI shows the login
      // screen and nothing is sent until the player acts.
      if (this.session !== undefined) {
        this.sendRaw({ type: 'hello', protocol: PROTOCOL_VERSION, session: this.session });
        if (this.rejoin !== null) this.sendRaw(this.rejoin);
      } else {
        this.options.onStatus('unauthenticated');
      }
    };

    socket.onmessage = (event) => {
      let parsed: ServerMessage;
      try {
        parsed = JSON.parse(String(event.data)) as ServerMessage;
      } catch {
        return; // a frame we cannot read is a server bug; nothing useful to do here
      }

      if (parsed.type === 'session') {
        this.session = parsed.token;
        this.options.onSession(parsed.token);
        if (this.rejoin !== null) this.sendRaw(this.rejoin);
      }

      // A refused session will not become valid on retry, so the connection
      // stops asking and the UI collects credentials instead.
      //
      // Only for 'unauthorized', which means the token names nobody. Its
      // neighbour 'not-authenticated' means this connection has not finished
      // saying who it is — a message that arrived early, and a good session
      // sitting right there. Treating the two alike is how a restart logged
      // people out: the socket dropped, something was sent a beat before the
      // handshake landed, and the answer to it deleted a token that had
      // twenty-nine days left on it.
      if (parsed.type === 'error' && parsed.code === 'unauthorized') {
        this.session = undefined;
        this.options.onSessionRejected();
        this.options.onStatus('unauthenticated');
      }

      this.options.onMessage(parsed);
    };

    socket.onclose = () => {
      if (this.closedByUser) return;
      this.options.onStatus('closed');
      this.retryTimer = setTimeout(() => this.open(), this.retryDelay);
      this.retryDelay = Math.min(this.retryDelay * 2, 8_000);
    };

    socket.onerror = () => socket.close();
  }

  private sendRaw(message: ClientMessage): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    }
  }

  register(email: string, password: string, displayName: string): void {
    this.sendRaw({ type: 'register', protocol: PROTOCOL_VERSION, email, password, displayName });
  }

  login(email: string, password: string): void {
    this.sendRaw({ type: 'login', protocol: PROTOCOL_VERSION, email, password });
  }

  logout(): void {
    this.sendRaw({ type: 'logout' });
    this.session = undefined;
    this.rejoin = null;
  }

  claimDaily(): void {
    this.sendRaw({ type: 'claim-daily' });
  }

  // --- recovery ------------------------------------------------------------
  //
  // These go out on an anonymous connection, which is the point: the player
  // sending them is the one who cannot sign in.

  verifyEmail(token: string): void {
    this.sendRaw({ type: 'verify-email', token });
  }

  requestReset(email: string): void {
    this.sendRaw({ type: 'request-reset', email });
  }

  resetPassword(token: string, password: string): void {
    this.sendRaw({ type: 'reset-password', token, password });
  }

  resendVerification(): void {
    this.sendRaw({ type: 'resend-verification' });
  }

  registerSng(tournamentId: string): void {
    this.sendRaw({ type: 'register-sng', tournamentId });
  }

  unregisterSng(tournamentId: string): void {
    this.sendRaw({ type: 'unregister-sng', tournamentId });
  }

  watch(tableId: string): void {
    this.rejoin = { type: 'watch', tableId };
    this.sendRaw(this.rejoin);
  }

  /**
   * Back to the lobby.
   *
   * Two things, and it used to do only the first: stop replaying the table on
   * reconnect, and tell the table to stop sending. Without the second the next
   * snapshot arrived a moment later and put the player straight back where
   * they had just left, which made the button look broken.
   */
  leaveTableView(): void {
    this.rejoin = null;
    this.sendRaw({ type: 'unwatch' });
  }

  sit(seat: number, buyIn: number): void {
    this.sendRaw({ type: 'sit', seat, buyIn });
  }

  stand(): void {
    this.sendRaw({ type: 'stand' });
  }

  rebuy(amount: number): void {
    this.sendRaw({ type: 'rebuy', amount });
  }

  setSittingOut(out: boolean): void {
    this.sendRaw({ type: 'sit-out', out });
  }

  act(handNumber: number, action: PlayerAction): void {
    this.sendRaw({ type: 'act', handNumber, action });
  }

  close(): void {
    this.closedByUser = true;
    if (this.retryTimer !== null) clearTimeout(this.retryTimer);
    this.socket?.close();
  }
}

/**
 * Where to dial.
 *
 * It was same-origin by definition — the client and the socket shared a host
 * because the client was always served by it. A packaged build is served by
 * itself, so the question moved to `server.ts`, which is the only place that
 * knows whether this is a web page or an application.
 */
export function defaultSocketUrl(): string {
  return socketUrl();
}
